<template>
  <div class="employee-app">
    <header class="employee-topbar">
      <router-link :to="{ name: 'personal-cabinet-overview' }" class="employee-brand" :aria-label="$t('nav.home')">
        <img class="employee-logo" src="@/assets/img/employee/logo/altezza-logo.svg" alt="Altezza Travel">
      </router-link>
      <div class="employee-topbar__actions">
        <language-switch />
        <button class="employee-menu-trigger" type="button" :aria-label="$t('nav.openMenu')" @click="menuOpen = true"><img src="@/assets/img/employee/menu-open.svg" alt=""></button>
      </div>
    </header>
    <main class="employee-content"><transition name="employee-fade" mode="out-in" @before-enter="onPageEntering" @after-enter="onPageEntered"><router-view /></transition></main>
    <nav class="employee-bottom-nav" :aria-label="$t('nav.employeeNavigation')">
      <router-link v-for="item in bottomItems" :key="item.name" :to="{ name: item.name }" exact-active-class="is-active">
        <span class="employee-bottom-nav__icon">
          <img class="employee-nav-icon__base" :src="item.image" alt="">
        </span><span>{{ $t(item.labelKey) }}</span>
        <b v-if="item.name === 'personal-cabinet-requests' && requestsCount">{{ badgeLabel }}</b>
      </router-link>
    </nav>
    <transition name="employee-menu">
      <aside v-if="menuOpen" class="employee-drawer" @click.self="menuOpen = false">
        <div class="employee-drawer__panel">
          <div class="employee-drawer__header">
            <div class="employee-brand">
              <img class="employee-logo" src="@/assets/img/employee/logo/altezza-logo.svg" alt="Altezza Travel">
            </div>
            <button type="button" :aria-label="$t('nav.closeMenu')" @click="menuOpen = false"><img src="@/assets/img/employee/menu-close.svg" alt=""></button>
          </div>
          <button
            v-if="workspaceRoute"
            class="employee-drawer__workspace"
            type="button"
            @click="openWorkspace"
          >
            <span class="employee-drawer__workspace-icon">
              <img src="@/assets/img/employee/workspace.svg" alt="">
            </span>
            <span class="employee-drawer__workspace-copy">
              <strong>{{ $t(workspaceLabelKey) }}</strong>
              <small>{{ $t('nav.workspaceHint') }}</small>
            </span>
            <span class="employee-drawer__workspace-arrow" aria-hidden="true">&#8594;</span>
          </button>
          <nav>
            <router-link v-for="item in drawerItems" :key="item.name" :to="{ name: item.name }" @click.native.prevent="navigateFromMenu(item)">
              <span class="employee-drawer__icon">
                <img class="employee-nav-icon__base" :src="item.image" alt="">
              </span>{{ $t(item.labelKey) }}
              <b v-if="item.name === 'personal-cabinet-requests' && requestsCount">{{ badgeLabel }}</b>
            </router-link>
          </nav>
          <button class="employee-drawer__logout" type="button" @click="onLogout">
            <span>{{ $t('nav.logout') }}</span>
            <img src="@/assets/img/logout.svg" alt="">
          </button>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
import { getPersonalCabinetNavigationStats } from '@/http'
import { clearAuthTokens } from 'axios-jwt'
import LanguageSwitch from '@/components/LanguageSwitch'
import { getWorkspacePageMobile, hasRole } from '@/router/personalCabinet'

export default {
  name: 'PersonalCabinetIndex',
  components: { LanguageSwitch },
  data() {
    return {
      menuOpen: false,
      closeMenuAfterNavigation: false,
      requestsCount: 0,
      bottomItems: [
        { labelKey: 'nav.home', name: 'personal-cabinet-overview', image: require('@/assets/img/employee/nav-home.svg') },
        { labelKey: 'nav.requests', name: 'personal-cabinet-requests', image: require('@/assets/img/employee/nav-requests.svg') },
        { labelKey: 'nav.payslips', name: 'personal-cabinet-payslips', image: require('@/assets/img/employee/nav-payslips.svg') },
        { labelKey: 'nav.profile', name: 'personal-cabinet-profile', image: require('@/assets/img/employee/nav-profile.svg') },
      ],
      drawerItems: [
        { labelKey: 'nav.home', name: 'personal-cabinet-overview', image: require('@/assets/img/employee/nav-home.svg') },
        { labelKey: 'nav.requests', name: 'personal-cabinet-requests', image: require('@/assets/img/employee/nav-requests.svg') },
        { labelKey: 'nav.payslips', name: 'personal-cabinet-payslips', image: require('@/assets/img/employee/nav-payslips.svg') },
        { labelKey: 'nav.profile', name: 'personal-cabinet-profile', image: require('@/assets/img/employee/nav-profile.svg') },
        { labelKey: 'nav.loans', name: 'personal-cabinet-loans', image: require('@/assets/img/employee/nav-loans.svg') },
        { labelKey: 'nav.advances', name: 'personal-cabinet-advances', image: require('@/assets/img/employee/nav-advances.svg') },
        { labelKey: 'nav.heslb', name: 'personal-cabinet-heslb', image: require('@/assets/img/employee/nav-heslb.svg') },
        { labelKey: 'nav.leave', name: 'personal-cabinet-leave', image: require('@/assets/img/employee/nav-leave.svg') },
        { labelKey: 'nav.medical', name: 'personal-cabinet-medical-reports', image: require('@/assets/img/employee/nav-medical.svg') },
        { labelKey: 'nav.misconduct', name: 'personal-cabinet-misconduct', image: require('@/assets/img/employee/nav-misconduct.svg') },
      ],
    }
  },
  computed: {
    badgeLabel() { return this.requestsCount > 99 ? '99+' : this.requestsCount },
    userGroups() {
      if (this.$store.state.userGroups.length) return this.$store.state.userGroups
      return JSON.parse(localStorage.getItem('userGroups')) || []
    },
    workspaceRoute() { return getWorkspacePageMobile(this.userGroups) },
    workspaceLabelKey() { return hasRole(this.userGroups, 'driver') ? 'nav.driverWorkspace' : 'nav.workspace' },
  },
  watch: {
    '$route.name'() { this.refreshBadge() },
  },
  created() {
    this.refreshBadge()
    this.$root.$on('personal-cabinet:requests-changed', this.refreshBadge)
  },
  beforeDestroy() { this.$root.$off('personal-cabinet:requests-changed', this.refreshBadge) },
  methods: {
    openWorkspace() {
      if (!this.workspaceRoute) return
      this.menuOpen = false
      this.$router.push({ name: this.workspaceRoute }).catch(() => {})
    },
    navigateFromMenu(item) {
      if (this.$route.name === item.name) {
        this.menuOpen = false
        return
      }
      this.closeMenuAfterNavigation = true
      this.$router.push({ name: item.name }, () => {}, () => {
        this.closeMenuAfterNavigation = false
        this.menuOpen = false
      })
    },
    onPageEntering() {
      window.scrollTo(0, 0)
    },
    onPageEntered() {
      if (!this.closeMenuAfterNavigation) return
      this.closeMenuAfterNavigation = false
      this.menuOpen = false
    },
    refreshBadge() {
      return getPersonalCabinetNavigationStats()
        .then(({ data }) => { this.requestsCount = Number(data.requests_count) || 0 })
        .catch(() => {})
    },
    onLogout() {
      const locale = localStorage.getItem('altezzaLocale')
      clearAuthTokens()
      localStorage.clear()
      if (locale) localStorage.setItem('altezzaLocale', locale)
      this.menuOpen = false
      this.$router.replace({ name: 'login' }).catch(() => {})
    },
  },
}
</script>

<style lang="sass">
.employee-app
  --employee-ink: #303126
  --employee-olive: #555545
  --employee-gold: #b5a13b
  --employee-cream: #f8f5ed
  --employee-border: #e7e1d4
  --page-loader-background: #f5f3ed
  --cabinet-page-min-height: calc(100vh - 101px - 80px - 42px - env(safe-area-inset-top) - env(safe-area-inset-bottom))
  --cabinet-page-min-height: calc(100dvh - 101px - 80px - 42px - env(safe-area-inset-top) - env(safe-area-inset-bottom))
  min-height: 100vh
  padding-bottom: calc(90px + env(safe-area-inset-bottom))
  background: #f5f3ed
  color: var(--employee-ink)
  font-family: 'Mazzard Soft H', 'Mazzard_Soft_H', 'SFUIText', sans-serif
.employee-topbar
  position: sticky
  top: 0
  z-index: 30
  display: flex
  align-items: center
  justify-content: space-between
  width: 100%
  max-width: 440px
  min-height: 101px
  margin: 0 auto
  padding: env(safe-area-inset-top) 18px 0
  background: #f5f3ed
.employee-brand
  display: inline-flex
  align-items: center
  width: 130px
  height: 42px
.employee-logo
  display: block
  width: 130px
  height: 42px
  object-fit: contain
.employee-topbar__actions
  display: flex
  align-items: center
  gap: 10px
.employee-language-switch
  display: grid
  grid-template-columns: repeat(2, 1fr)
  width: 100px
  height: 34px
  padding: 3px
  border-radius: 8px
  background: #eee8dc
  button
    display: flex
    width: 47px
    height: 28px
    min-width: 0
    align-items: center
    justify-content: center
    gap: 4px
    padding: 0
    border: 0
    border-radius: 6px
    color: #8e806a
    font-family: 'Mazzard Soft H', sans-serif
    font-size: 10px
    font-weight: 500
    line-height: 1
    opacity: 1
    > img
      display: block
      width: 16px
      height: 16px
      border-radius: 50%
      object-fit: cover
    &.is-active
      background: #fff
      color: #49463d
      box-shadow: 0 1px 3px rgba(73, 70, 61, .16)
    &[disabled]
      cursor: default
.employee-menu-trigger
  width: 40px
  height: 40px
  img
    display: block
    width: 40px
    height: 40px
.employee-content
  position: relative
  width: 100%
  max-width: 440px
  min-height: calc(100vh - 150px)
  margin: 0 auto
  padding: 18px 18px 24px
.cabinet-page
  position: relative
  min-height: var(--cabinet-page-min-height)
  &.is-loading
    height: var(--cabinet-page-min-height)
    overflow: hidden
.employee-bottom-nav
  position: fixed
  right: 0
  bottom: 0
  left: 0
  width: 100%
  max-width: 440px
  margin: 0 auto
  z-index: 35
  display: grid
  grid-template-columns: repeat(4, 1fr)
  min-height: calc(80px + env(safe-area-inset-bottom))
  padding: 11px 8px env(safe-area-inset-bottom)
  border: 1px solid #f1ebdf
  background: #faf8f4
  a
    position: relative
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 8px
    color: #8e806a
    font-size: 11px
    font-weight: 600
    letter-spacing: .55px
    text-transform: uppercase
    text-align: center
    &.is-active
      color: #37382e
    b
      position: absolute
      top: -4px
      left: calc(50% + 3px)
      display: grid
      place-items: center
      min-width: 20px
      height: 20px
      padding: 0 4px
      border: 0
      border-radius: 12px
      background: #8eb671
      color: #fff
      font-family: Inter, sans-serif
      font-size: 12px
      text-align: center
.employee-bottom-nav__icon
  position: relative
  display: block
  width: 20px
  height: 20px
.employee-nav-icon__base
  display: block
  width: 20px
  height: 20px
.employee-drawer
  position: fixed
  inset: 0
  z-index: 60
  background: #faf8f4
.employee-drawer__panel
  display: flex
  flex-direction: column
  width: 100%
  max-width: 440px
  height: 100%
  margin-left: auto
  padding: calc(31px + env(safe-area-inset-top)) 30px 30px
  border: 1px solid #f1ebdf
  background: #faf8f4
  box-shadow: none
.employee-drawer__header
  display: flex
  align-items: center
  justify-content: space-between
  min-height: 73px
  margin-bottom: 0
  border-bottom: 1px solid #ded8cc
  button
    width: 40px
    height: 40px
    img
      display: block
      width: 40px
      height: 40px
.employee-drawer__workspace
  flex-shrink: 0
  display: grid
  grid-template-columns: 38px minmax(0, 1fr) auto
  align-items: center
  column-gap: 12px
  width: 100%
  min-height: 68px
  margin: 16px 0 8px
  padding: 10px 15px
  border-radius: 16px
  background: #77b017
  color: #fff
  font-family: 'Mazzard Soft H', sans-serif
  text-align: left
  box-shadow: 0 7px 18px rgba(119, 176, 23, .2)
  transition: transform .18s ease, box-shadow .18s ease
  &:active
    transform: translateY(1px)
    box-shadow: 0 3px 10px rgba(119, 176, 23, .18)
.employee-drawer__workspace-icon
  display: grid
  place-items: center
  width: 38px
  height: 38px
  border-radius: 11px
  background: rgba(255, 255, 255, .18)
  img
    width: 22px
    height: 22px
.employee-drawer__workspace-copy
  display: flex
  min-width: 0
  flex-direction: column
  gap: 2px
  strong
    font-size: 16px
    font-weight: 600
    line-height: 1.2
  small
    overflow: hidden
    font-size: 11px
    font-weight: 400
    line-height: 1.2
    opacity: .85
    text-overflow: ellipsis
    white-space: nowrap
.employee-drawer__workspace-arrow
  font-family: Arial, sans-serif
  font-size: 24px
  line-height: 1
.employee-drawer nav
  flex: 1
  display: grid
  align-content: start
  gap: 0
  overflow-y: auto
  scrollbar-width: none
  &::-webkit-scrollbar
    display: none
  a
    display: grid
    grid-template-columns: 20px minmax(0, 1fr) auto
    column-gap: 15px
    align-items: center
    min-height: 60px
    padding: 0
    border-bottom: 1px solid #ded8cc
    color: #49463d
    font-size: 16px
    font-weight: 500
    &.router-link-active
      color: #2f3028
    > span
      display: grid
      place-items: center
      width: 20px
      height: 20px
    b
      min-width: 22px
      padding: 3px 6px
      border-radius: 12px
      background: #8eb671
      color: #fff
      font-size: 12px
      text-align: center
.employee-drawer__logout
  flex-shrink: 0
  display: flex
  align-items: center
  justify-content: space-between
  min-height: 60px
  padding: 0
  border-top: 1px solid #ded8cc
  color: #49463d
  font-family: 'Mazzard Soft H', sans-serif
  font-size: 16px
  font-weight: 600
  text-align: left
  img
    width: 20px
    height: 20px
    padding: 3px
    border-radius: 6px
    background: #555545
.employee-drawer__icon
  position: relative
  .employee-nav-icon__base
    width: 20px
    height: 20px
    object-fit: contain
.employee-menu-enter-active, .employee-menu-leave-active
  transition: opacity .22s
.employee-menu-enter, .employee-menu-leave-to
  opacity: 0
.employee-fade-enter-active, .employee-fade-leave-active
  transition: opacity .16s, transform .16s
.employee-fade-enter, .employee-fade-leave-to
  opacity: 0
  transform: translateY(4px)
.employee-app
  .title
    color: #2f3028
    font-family: 'Mazzard Soft H', 'SFUIText', sans-serif
    font-size: 28px
    font-weight: 600
    line-height: 1.15
    letter-spacing: -.3px
  .btn
    border-radius: 24px
  .btn--default
    background-color: #555545
  .cabinet-profile__cards, .payslips__list
    gap: 11px
  .info-card, .payslip-card, .payslips-summary
    border-color: #e8e2d6
    border-radius: 18px
    background: #fffefa
  .employee-brand
    width: 120px
    transform: scale(.923)
    transform-origin: left center
  .employee-topbar__actions
    gap: 6px
  .employee-language-switch
    width: 90px
    button
      width: 42px
  .employee-menu-trigger,
  .employee-menu-trigger img
    width: 34px
    height: 34px
@media (min-width: 761px)
  .employee-bottom-nav
    right: calc(50% - 280px)
    left: calc(50% - 280px)
    border-right: 1px solid #e8e2d7
    border-left: 1px solid #e8e2d7
</style>
